import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients } from '@/api/endpoints';
import type { Client } from '@/types';
import { Icon } from '@/components/Icon';
import { ClientFormModal } from '@/components/ClientFormModal';
import { Pagination, usePaginated } from '@/components/Pagination';

export function ClientsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Client[] | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  const reload = (s = debounced) => {
    setRows(null);
    setErr(null);
    clients.list(s || undefined).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(debounced); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debounced]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c: Client) => { setEditing(c); setModalOpen(true); };

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'clients' });

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Klienci</h1>
          <div className="text-ink-400 text-[13px]">Lista klientów (gminy, spółki, osoby prywatne)</div>
        </div>
        <button onClick={openNew} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5" /> Nowy klient
        </button>
      </header>

      <div className="card">
        <div className="card-head">
          <div className="flex items-center gap-2 bg-ink-50 border border-ink-100 px-2.5 py-1.5 rounded-md text-ink-400 w-full max-w-[420px]">
            <Icon name="search" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Szukaj po nazwie, NIP-ie, miejscowości…"
              className="flex-1 border-0 bg-transparent outline-none text-[13px] text-ink-800"
            />
          </div>
        </div>
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">
              {debounced ? 'Brak klientów pasujących do wyszukiwania.' : 'Brak klientów. Dodaj pierwszego.'}
            </div>
          )}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>NIP</th>
                  <th>Adres</th>
                  <th>Kontakt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(c => (
                  <tr key={c.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => navigate(`/klienci/${c.id}`)}>
                    <td>
                      <div className="font-semibold text-ink-900">{c.name}</div>
                      {c.remarks && <div className="text-[11px] text-ink-400 truncate max-w-[400px]">{c.remarks}</div>}
                    </td>
                    <td className="font-mono text-[12px] text-ink-600">{c.nip ?? '—'}</td>
                    <td className="text-[12px] text-ink-700">
                      {c.addressLine1 || c.city ? (
                        <>
                          {c.addressLine1 && <div>{c.addressLine1}</div>}
                          <div className="text-ink-500">{[c.postalCode, c.city].filter(Boolean).join(' ')}</div>
                        </>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-[12px]">
                      {c.email && <div className="text-ink-700">{c.email}</div>}
                      {c.phone && <div className="text-ink-500 font-mono">{c.phone}</div>}
                      {!c.email && !c.phone && <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openEdit(c)} className="btn btn-secondary btn-sm" title="Edytuj dane">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      <Icon name="chevron" className="text-ink-300 inline ml-2" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      <ClientFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => reload()}
      />
    </div>
  );
}
